import React, { useMemo, useState } from "react";
import { FaSlidersH, FaTimes } from "react-icons/fa";

const categoryConfig = {
  Console: {
    conditions: ["Used", "New"],
    options: [
      "PS5",
      "PS4",
      "Nintendo Switch",
      "Nintendo Switch 2",
      "Xbox Series",
      "Lenovo Legion Go",
      "Lenovo Legion Go 2",
      "ROG Ally",
      "Steam Deck",
      "Steam Deck OLED",
    ],
  },
  "Game CDs": {
    conditions: ["Used" , "New"],
    options: [
      "PS5",
      "PS4",
      "Xbox Series X",
      "Nintendo Switch",
      "Nintendo Switch 2",
    ],
  },
  "Phone & Laptop": {
    conditions: ["Used" , "New"],
    options: [],
  },
  Controller: {
    conditions: ["Used" , "New"],
    options: [],
  },
  Games: {
    conditions: ["Used" , "New"],
    options: [],
  },
  Accessories: {
    conditions: ["Used" , "New"],
    options: [],
  },
  "Gift Cards": {
    conditions: ["Used" , "New"],
    options: [],
  },
  "VR Headsets": {
    conditions: ["Used" , "New"],
    options: [],
  },
};

const brands = [
  "All",
  "Sony",
  "Microsoft",
  "Nintendo",
  "Lenovo",
  "ASUS",
  "Valve",
  "Meta",
  "SteelSeries",
];

const Filters = ({ filters, setFilters }) => {
  const [open, setOpen] = useState(false);

  const categories = useMemo(() => ["All", ...Object.keys(categoryConfig)], []);

  const selectedCategoryData = useMemo(() => {
    return categoryConfig[filters.category] || { conditions: [], options: [] };
  }, [filters.category]);

  const update = (key, value) => {
    setFilters((prev) => {
      const nextValue = value === "All" ? "" : value;

      if (key === "category") {
        return {
          ...prev,
          category: nextValue,
          condition: "",
          platform: "",
        };
      }

      return {
        ...prev,
        [key]: nextValue,
      };
    });
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      category: "",
      condition: "",
      platform: "",
      brand: "",
      minPrice: "",
      maxPrice: "",
      rating: "",
      sort: "",
      limit: 12,
    });
  };

  const maxRangeValue = useMemo(() => {
    const value = Number(filters.maxPrice);
    return Number.isFinite(value) && value > 0 ? value : 1000000;
  }, [filters.maxPrice]);

  const Panel = (
    <aside className="w-full space-y-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      {/* Search */}
      <div>
        <h3 className="mb-2 font-semibold text-gray-900">Search</h3>
        <input
          type="text"
          placeholder="Search products..."
          value={filters.search || ""}
          onChange={(e) => update("search", e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
        />
      </div>

      {/* Categories */}
      <div>
        <h3 className="mb-2 font-semibold text-gray-900">Categories</h3>
        <div className="flex flex-col gap-2">
          {categories.map((cat) => {
            const active =
              (cat === "All" && !filters.category) || filters.category === cat;

            return (
              <button
                key={cat}
                type="button"
                onClick={() => update("category", cat)}
                className={`rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${
                  active
                    ? "border-red bg-red text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Condition */}
      {selectedCategoryData.conditions.length > 0 && (
        <div>
          <h3 className="mb-2 font-semibold text-gray-900">Condition</h3>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => update("condition", "All")}
              className={`rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${
                !filters.condition
                  ? "border-red bg-red text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              All
            </button>

            {selectedCategoryData.conditions.map((condition) => {
              const active = filters.condition === condition;

              return (
                <button
                  key={condition}
                  type="button"
                  onClick={() => update("condition", condition)}
                  className={`rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${
                    active
                      ? "border-red bg-red text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {condition}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Platform / Model */}
      {selectedCategoryData.options.length > 0 && (
        <div>
          <h3 className="mb-2 font-semibold text-gray-900">
            {filters.category === "Game CDs" ? "CD Platform" : "Platform"}
          </h3>

          <select
            value={filters.platform || ""}
            onChange={(e) => update("platform", e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400"
          >
            <option value="">All</option>
            {selectedCategoryData.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Brands */}
      <div>
        <h3 className="mb-2 font-semibold text-gray-900">Brands</h3>
        <select
          value={filters.brand || ""}
          onChange={(e) => update("brand", e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400"
        >
          {brands.map((brand) => (
            <option key={brand} value={brand === "All" ? "" : brand}>
              {brand}
            </option>
          ))}
        </select>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="mb-2 font-semibold text-gray-900">Price Range</h3>

        <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
          <span>₦0</span>
          <span>₦1,000,000</span>
        </div>

        <input
          type="range"
          min={0}
          max={1000000}
          step={5000}
          value={maxRangeValue}
          onChange={(e) => update("maxPrice", e.target.value)}
          className="w-full"
          style={{ accentColor: "var(--color-red, #E00A0C)" }}
        />

        <div className="mt-3 flex gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.minPrice || ""}
            onChange={(e) => update("minPrice", e.target.value)}
            className="w-1/2 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.maxPrice || ""}
            onChange={(e) => update("maxPrice", e.target.value)}
            className="w-1/2 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>
      </div>

      {/* Sort */}
      <div>
        <h3 className="mb-2 font-semibold text-gray-900">Sort By</h3>
        <select
          value={filters.sort || ""}
          onChange={(e) => update("sort", e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400"
        >
          <option value="">Default</option>
          <option value="price-low">Price: Low to High</option>
          <option value="price-high">Price: High to Low</option>
          <option value="rating">Top Rated</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {/* Clear */}
      <button
        type="button"
        onClick={clearFilters}
        className="w-full rounded-xl border border-red py-2.5 text-sm font-semibold text-red transition hover:bg-red hover:text-white"
      >
        Clear Filters
      </button>
    </aside>
  );

  return (
    <>
      {/* Mobile / Tablet Filter Button */}
      <div className="mb-3 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
          <span className="font-semibold text-gray-900">Filters</span>
          <FaSlidersH className="text-gray-700" />
        </button>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block lg:w-72">
        <div className="sticky top-24">{Panel}</div>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-50 transition lg:hidden ${
          open ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${
            open ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setOpen(false)}
        />

        <div
          className={`absolute left-0 top-0 h-full w-[88%] max-w-sm bg-white shadow-xl transition-transform duration-300 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-16 items-center justify-between border-b border-gray-100 px-4">
            <p className="font-bold text-gray-900">Filters</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-xl p-2 hover:bg-gray-100"
              aria-label="Close filters"
            >
              <FaTimes />
            </button>
          </div>

          <div className="h-[calc(100%-64px)] overflow-y-auto p-4">
            {Panel}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-4 w-full rounded-xl bg-red py-2.5 font-semibold text-white transition hover:opacity-90"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Filters;